import { Text, useToast } from "@chakra-ui/react";
import { FormEvent, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { $map } from "foundation-ts/array";
import { $UUID, $count, $length, $ok, $value } from "foundation-ts/commons";
import { $trim } from "foundation-ts/strings";
import { TSDate } from "foundation-ts/tsdate";
import { Nullable, UUID } from "foundation-ts/types";
import { LockedAccount, SectionCategory, SessionActorType } from "@bcasmart/doxecure-commons/doxecureTypes";
import { SectionDto, SessionDto, SessionsQueryRestrictions } from "@bcasmart/doxecure-commons/doxecureClientTypes";

import { PageShellWithThreeColumns } from "@components/PageShellWithThreeColumns/PageShellWithThreeColumns";
import { ConfirmModal } from "@components/Modals/ConfirmModal";
import { DocumentsViewer } from "@containers/generics/DocumentsViewer";
import { SectionInfos, SectionsList } from "@containers/generics/SectionsList";
import { useAuthContext } from "@hooks/useAuthContext";
import useSections from "@hooks/useSections";
import useDeleteSession from "@hooks/useDeleteSession";
import useUpdateToBeSignedSession from "@hooks/useUpdateToBeSignedSession";
import { SectionsListQueryID } from "@hooks/hooksConstants";
import { optlog, trueOrEmptyCard } from "@utils/functions";
import { defaultErrorToast } from "@utils/toast";

export interface DashboardSession extends SessionDto {
    selected?:boolean ;
    immutableDocuments?:boolean ;
}

export interface DashboardSection extends SectionDto {
    selectionInside:boolean ;
    sessions: DashboardSession[];
}

export type SessionFilter = (session: SessionDto, section:SectionCategory) => boolean ;
export interface DashboardProps {
    category?: SectionCategory ;
    roles: SessionActorType|SessionActorType[] ;
    immutableDocuments?:Nullable<boolean> ;
    title: string ;
    emptyMessage?:string ;
    activateSearch?: boolean ;
    search?: string ;
    acceptsSignature?:boolean ;
    updateDuration?:number ;
    lockedAccount?: LockedAccount;
    sectionInfos: (category: SectionCategory) => SectionInfos ;
    sessionFilter?:SessionFilter ;
}

export const Dashboard = (props: DashboardProps) => {
    const { roles, title, emptyMessage, immutableDocuments, search = '', category = undefined, activateSearch = false, acceptsSignature = false, updateDuration = 30, lockedAccount, sectionInfos, sessionFilter = undefined } = props;
    const { connectedProfileId } = useAuthContext();
    const [searchCriteria, setSearchCriteria] = useState(activateSearch && $trim(search).length?$trim(search):'');
    const { data, isError, isLoading } = useSections(connectedProfileId, roles, SessionsQueryRestrictions.None, category, searchCriteria, true);
    const { mutateAsync: patchToBeSignedSession } = useUpdateToBeSignedSession();
    const [sections, setSections] = useState<DashboardSection[]>([]);
    const [selectedSession, setSelectedSession] = useState<DashboardSession | undefined>(undefined);
    const forcedImmutableDocuments = $ok(immutableDocuments) ? immutableDocuments! : (category === 'draft' ? false : undefined) ;
    const { mutateAsync: deleteSession } = useDeleteSession();
    const [modalText, setModalText] = useState('');
    const [modalSessionID, setModalSessionID] = useState<Nullable<UUID>>(undefined) ;
    const toast = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation() ;
    const searchParams = new URLSearchParams(location.search) ;

    const reloadSession = () => {
        optlog('Checking for modifications');
        if (!$ok(selectedSession)) {
            optlog('**** RESET QUERY INTERVAL ****')
            queryClient.invalidateQueries(SectionsListQueryID);
        }
    }

    const resetAndBack = () => {
        const [sects, sess] = visibleItems(data, forcedImmutableDocuments, undefined, sessionFilter, []) ;
        setSections(sects) ;
        setSelectedSession(sess);

        optlog(`--- Dashboard reseted with: ${sects.length} section(s)`, sess, $map(sects.first()?.sessions, s => `${s.title} [${s.selected}]`)) ;
    };

    const handleSelection = (sessionID:Nullable<UUID>) => {
        const [sects, sess] = visibleItems(data, forcedImmutableDocuments, sessionID, sessionFilter, []) ;
        setSections(sects) ;
        setSelectedSession(sess);

        optlog(`--- Dashboard did select session: ${sess?.apid}`) ;
    } ;

    const handleEdition = (sessionID:Nullable<UUID>) => {
        navigate(`/app/newSignature/${sessionID}`, { replace: true });
    }

    const handleDelete = (session:UUID|SessionDto) => {
        const s = typeof session === 'string' ? sessionWithSessionID(data, session) : session ;
        if ($ok(s)) {
            setModalText(`Voulez-vous vraiment supprimer le brouillon ${s!.title} ?`) ;
            setModalSessionID(s!.apid!) ;
        }
        else { toast(defaultErrorToast("Impossible de supprimer le brouillon: session introuvable.")) ; }
    }
    
    const handleExternalSignature = (_session:SessionDto) => {
        resetAndBack() ;
    }

    const handleExtendSessionDate = async (newDate: Nullable<TSDate>) => {
        if (!$ok(selectedSession?.apid)) { return; }
        if ($ok(newDate)) {
            patchToBeSignedSession({ sessionId: selectedSession!.apid!, newDate: newDate! });
        } else {
            toast(defaultErrorToast("La nouvelle date de fin de session est invalide"));
        }
    }

    const effectiveSessionDeletion = (_sessionID:Nullable<UUID>) => {
        deleteSession(modalSessionID!) ;
        setModalSessionID(undefined) ;
        setModalText("") ;
    }

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        if (e.type === "submit") {
            setSearchCriteria(e.target[0].value);
        }
    };

    useEffect(() => {
        const sid = searchParams.get("sid") ;
        const [sects, sess] = visibleItems(data, forcedImmutableDocuments, $ok(sid) ? $UUID(sid) : undefined, sessionFilter, []) ;
        setSections(sects) ;
        setSelectedSession(sess);
        optlog(`--- Dashboard initialized with: ${sects.length} section(s) for profileID '${connectedProfileId}'`) ;
    }, [data]); //, tabOrWindowVisible]);

    useEffect(() => {
        const interval = setInterval(reloadSession, 1000 * updateDuration);
        return () => clearInterval(interval);
    }, [selectedSession]);

    return (
        <>
            <PageShellWithThreeColumns
                centralContent={
                    <SectionsList
                        title={title}
                        sections={sections}
                        emptyMessage={emptyMessage}
                        selectedSessionID={selectedSession?.apid}
                        searchString={activateSearch?searchCriteria:undefined}
                        sectionInfos={sectionInfos}
                        onEdit={handleEdition}
                        onDelete={handleDelete}
                        onSearch={activateSearch?handleSearch:undefined}
                        onSelect={handleSelection}
                    />
                }
                rightContent={
                    trueOrEmptyCard(
                        !isError && !isLoading && $count(sections)>0,
                        $ok(selectedSession) ?
                            (<DocumentsViewer
                                sessionId={selectedSession!.apid!}
                                profileId={connectedProfileId}
                                acceptsSignature={!!acceptsSignature}
                                validityDate={$value(selectedSession?.validity?.end?.toLocaleString("%d/%m/%Y"), '')}
                                immutableDocuments={!!(selectedSession!.immutableDocuments)}
                                senderName={$ok(selectedSession!.expeditor) ? `${selectedSession!.expeditor!.firstName} ${selectedSession!.expeditor!.lastName.toUpperCase()}` : "Inconnu"}
                                signatureMode={selectedSession?.signatures?.first()?.signatureMode}
                                lockedAccount={lockedAccount}
                                resetAndBack={resetAndBack}
                                deleteSession={() => handleDelete(selectedSession!)}
                                onExternalSignature={() => handleExternalSignature(selectedSession!)}
                                editSession={()=>handleEdition(selectedSession!.apid!)}
                                extendEndSessionDate={handleExtendSessionDate}
                            />) :
                            (<Text display="flex" flexDirection="column" justifyContent="center" height="100%" textAlign="center" color="lightgrey">
                                Sélectionnez une section pour afficher ses documents
                            </Text>)
                    )
                }
                isOpen={$ok(selectedSession)}
            />
            <ConfirmModal
                bodyText={modalText}
                isOpen={$length(modalText) > 0}
                onClose={() => setModalText('')}
                onAccept={() => effectiveSessionDeletion(modalSessionID)}
            />
        </>
    );
};

function sessionWithSessionID(source:Nullable<SectionDto[]>, sessionID?:Nullable<UUID>):DashboardSession|undefined {
    if (!$count(source) || !$ok(sessionID)) { return undefined ; }
    for (let section of source!) {
        const found = section.sessions?.find(session => session.apid === sessionID) ;
        if ($ok(found)) { return found ;}
    }
    return undefined ;
}

function visibleItems(source:Nullable<SectionDto[]>, forceImmutable:Nullable<boolean>, selectedSessionID:Nullable<UUID>, sessionFilter:Nullable<SessionFilter>, blacklisteds:UUID[]):[DashboardSection[], DashboardSession|undefined] {
    if (!$count(source)) { return [[], undefined /*, []*/] ; }
    let theSelectedSession:DashboardSession|undefined = undefined ;
    
    const filter = $value(sessionFilter, (_session:SessionDto, _cat:SectionCategory) => true) ;
    const visibleSections = source!.map(section => {
        const immutable = $ok(forceImmutable) ? forceImmutable! : section.category !== 'draft' ;
        let selectionInside = false ;
        const visibleSessions = $map(section.sessions, session => {
            if (!filter(session, section.category) || ($ok(session.apid) && blacklisteds.includes(session.apid!))) { return null ; }
            const sessionSelected = session.apid === selectedSessionID ;
            selectionInside ||= sessionSelected ;
            const visibleSession = { ...session, selected:sessionSelected, immutableDocuments:immutable} ;
            if (visibleSession.selected) { 
                theSelectedSession = visibleSession ; 
            }
            return visibleSession ;
        }) ;
        return { ...section, selectionInside:selectionInside, sessions:visibleSessions } ; // we may not have any sessions in here
    }) ;

    return [visibleSections, theSelectedSession] ;
}

